<template>
  <div class="main-container p-3">
    <KidHeader :profileId="profileId" :serialNumber="serialNumber" />

    <!-- Back to Schedule List -->
    <div class="back-home text-left mb-3">
      <a class="text-black" href="javascript:void(0)" @click="$router.push({ path: '/schedule', query: { profileId: this.profileId, serialNumber: serialNumber } })">
        <i class="fas fa-arrow-left"></i> <b>Back to Schedules</b>
      </a>
    </div>

    <!-- Schedule Form -->
    <div class="schedule-form mt-2">
      <div class="card">
        <div class="card-body">

          <!-- Schedule Label -->
          <div class="form-group mb-3 text-left">
            <label class="font-weight-bold">
              <i class="fas fa-tag"></i> Schedule Name <span class="text-danger">*</span>
            </label>
            <input
              type="text"
              class="form-control form-control-sm"
              v-model="currentSchedule.label"
              placeholder="e.g., School Time, Bedtime, Weekend Gaming"
              :class="{ 'is-invalid': errors.label }"
            />
            <small class="text-muted">Give your schedule a short descriptive name</small>
            <div v-if="errors.label" class="invalid-feedback">{{ errors.label }}</div>
          </div>

          <!-- Schedule Type Selector (compact) -->
          <div class="form-group mb-3 text-left">
            <label class="font-weight-bold d-block mb-2">
              <i class="fas fa-layer-group"></i> Schedule Type <span class="text-danger">*</span>
            </label>

            <div class="type-chips">
              <button
                type="button"
                class="type-chip"
                :class="{ active: currentSchedule.type === 'custom' }"
                @click="selectScheduleType('custom')"
                title="Custom - choose days manually"
              >
                <i class="fas fa-sliders-h"></i>
                <div class="chip-label">Custom</div>
              </button>

              <button
                type="button"
                class="type-chip"
                :class="{ active: currentSchedule.type === 'weekday' }"
                @click="selectScheduleType('weekday')"
                title="Mon - Fri"
              >
                <i class="fas fa-briefcase"></i>
                <div class="chip-label">Weekday</div>
              </button>

              <button
                type="button"
                class="type-chip"
                :class="{ active: currentSchedule.type === 'weekend' }"
                @click="selectScheduleType('weekend')"
                title="Sat - Sun"
              >
                <i class="fas fa-calendar-week"></i>
                <div class="chip-label">Weekend</div>
              </button>
            </div>
          </div>

          <!-- Day Selection (modern bubbles) -->
          <div class="form-group mb-3 text-left">
            <label class="font-weight-bold d-block mb-2">
              <i class="fas fa-calendar-day"></i> Select Days <span class="text-danger">*</span>
            </label>

            <div class="days-bubbles">
              <button
                type="button"
                v-for="day in allDays"
                :key="day.value"
                class="day-bubble"
                :class="{ active: currentSchedule.days.includes(day.value) }"
                @click="toggleDay(day.value)"
              >
                {{ day.short }}
              </button>
            </div>

            <div class="mt-2">
              <small v-if="errors.days" class="text-danger">{{ errors.days }}</small>
              <small v-else class="text-muted">Tap days to include them in the schedule.</small>
            </div>
          </div>

          <!-- Restriction Settings (Multiple) -->
          <div class="form-group mb-3 text-left">
            <label class="font-weight-bold d-block mb-2">
              <i class="fas fa-clock"></i> Restriction Settings <span class="text-danger">*</span>
            </label>

            <!-- Loop through slots -->
            <div v-for="(slot, index) in currentSchedule.slots" :key="index" class="compact-slot mb-3">
              <!-- Slot Header with Delete Button -->
              <div class="slot-header" v-if="currentSchedule.slots.length > 1">
                <span class="slot-number">Restriction #{{ index + 1 }}</span>
                <button type="button" class="btn-delete-slot" @click="removeSlot(index)">
                  <i class="fas fa-trash-alt"></i>
                </button>
              </div>

              <!-- 1. RESTRICTION MODE -->
              <div>
                <label class="small font-weight-bold d-block mb-1">Restriction Mode</label>
                <div class="compact-mode">
                  <button
                    type="button"
                    class="mode-btn sleep-btn"
                    :class="{ active: slot.mode === 'sleep' }"
                    @click="slot.mode = 'sleep'"
                  >
                    <i class="fas fa-ban"></i> Sleep Mode
                  </button>

                  <button
                    type="button"
                    class="mode-btn limit-btn"
                    :class="{ active: slot.mode === 'limit' }"
                    @click="slot.mode = 'limit'"
                  >
                    <i class="fas fa-hourglass-half"></i> Daily Limit
                  </button>

                  <button
                    type="button"
                    class="mode-btn apps-btn"
                    :class="{ active: slot.mode === 'apps' }"
                    @click="slot.mode = 'apps'"
                  >
                    <i class="fas fa-mobile-alt"></i> Apps Restriction
                  </button>
                </div>
              </div>

              <!-- 2. MODE DESCRIPTION / NOTE -->
              <div class="mt-2">
                <div v-if="slot.mode === 'sleep'" class="mode-note mode-note-sleep">
                  <i class="fas fa-info-circle"></i>
                  <span><strong>Sleep Mode:</strong> All apps will be disabled during the selected time period.</span>
                </div>
                <div v-else-if="slot.mode === 'limit'" class="mode-note mode-note-limit">
                  <i class="fas fa-info-circle"></i>
                  <span><strong>Daily Limit:</strong> All apps will be disabled once the total usage time is reached.</span>
                </div>
                <div v-else class="mode-note mode-note-apps">
                  <i class="fas fa-info-circle"></i>
                  <span><strong>Apps Restriction:</strong> Selected app categories will be disabled during the specified time period.</span>
                </div>
              </div>

              <!-- 3. TIME FIELDS (FOR SLEEP MODE AND APPS RESTRICTION) -->
              <div v-if="slot.mode === 'sleep' || slot.mode === 'apps'" class="slot-row mt-3">
                <div class="slot-col">
                  <label class="small font-weight-bold">From</label>
                  <input type="time" class="form-control form-control-sm" v-model="slot.startTime" />
                </div>
                <div class="slot-col">
                  <label class="small font-weight-bold">To</label>
                  <input type="time" class="form-control form-control-sm" v-model="slot.endTime" />
                </div>
              </div>

              <!-- 4. LIMIT INPUT (ONLY FOR DAILY LIMIT MODE) -->
              <div v-if="slot.mode === 'limit'" class="mt-3">
                <label class="small font-weight-bold d-block mb-1">Daily Usage Limit (minutes)</label>
                <input
                  type="number"
                  class="form-control form-control-sm"
                  v-model.number="slot.limit"
                  min="5"
                  max="480"
                  step="5"
                  placeholder="e.g., 60"
                />
                <small class="text-muted d-block mt-1">Minimum 5 minutes, Maximum 480 minutes (8 hours)</small>
              </div>

              <!-- 5. APP CATEGORIES (ONLY FOR APPS RESTRICTION MODE) -->
              <div v-if="slot.mode === 'apps'" class="mt-3">
                <label class="small font-weight-bold d-block mb-2">Select App Categories to Restrict</label>
                <div class="app-categories">
                  <div v-for="category in appCategories" :key="category.id" class="category-wrapper">
                    <!-- Category Header (Clickable) -->
                    <div 
                      class="category-item" 
                      :class="{ 'expanded': expandedCategories[index + '-' + category.id] }"
                      @click="toggleCategoryExpand(index, category.id)"
                    >
                      <div class="category-info">
                        <i :class="category.icon" class="category-icon"></i>
                        <span class="category-name">{{ category.name }}</span>
                        <span class="app-count">({{ category.apps.length }} apps)</span>
                      </div>
                      <div class="category-actions">
                        <div class="custom-control custom-switch" @click.stop>
                          <input 
                            type="checkbox" 
                            class="custom-control-input" 
                            :id="'cat-' + index + '-' + category.id"
                            v-model="slot.categories"
                            :value="category.id"
                          />
                          <label class="custom-control-label" :for="'cat-' + index + '-' + category.id"></label>
                        </div>
                        <i class="fas fa-chevron-down expand-icon" :class="{ 'rotated': expandedCategories[index + '-' + category.id] }"></i>
                      </div>
                    </div>

                    <!-- Apps List (Expandable) -->
                    <div v-show="expandedCategories[index + '-' + category.id]" class="apps-list">
                      <div v-for="app in category.apps" :key="app.id" class="app-item">
                        <span class="app-name">{{ app.name }}</span>
                        <div class="custom-control custom-switch">
                          <input 
                            type="checkbox" 
                            class="custom-control-input" 
                            :id="'app-' + index + '-' + app.id"
                            v-model="slot.restrictedApps"
                            :value="app.packageName"
                          />
                          <label class="custom-control-label" :for="'app-' + index + '-' + app.id"></label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Add More Restriction Button -->
            <button type="button" class="btn-add-more" @click="addSlot()">
              <i class="fas fa-plus-circle"></i> Add More Restriction
            </button>

            <div class="mt-2">
              <small v-if="errors.slots" class="text-danger">{{ errors.slots }}</small>
            </div>
          </div>

          <!-- Schedule Status -->
          <div class="form-group mb-0 text-left">
            <div class="custom-control custom-switch">
              <input
                type="checkbox"
                class="custom-control-input"
                id="scheduleActive"
                v-model="currentSchedule.isActive"
              />
              <label class="custom-control-label font-weight-bold" for="scheduleActive">
                <i class="fas fa-power-off"></i> Activate this schedule immediately
              </label>
            </div>
            <small class="text-muted d-block mt-1">You can enable or disable this schedule anytime from the list</small>
          </div>

        </div>
      </div>

      <!-- Action Buttons -->
      <div class="action-buttons mt-3 d-flex justify-content-end">
        <button
          type="button"
          class="btn btn-secondary btn-sm"
          @click="cancelSchedule()"
        >
          <i class="fas fa-times"></i> Cancel
        </button>

        <button
          type="button"
          class="btn btn-primary btn-sm ml-2"
          @click="saveSchedule()"
          :disabled="!isValidSchedule"
        >
          <i class="fas fa-save"></i> {{ isEditMode ? 'Update Schedule' : 'Save Schedule' }}
        </button>
      </div>
    </div>

    <KidFooter :profileId="profileId" :serialNumber="serialNumber" />
  </div>
</template>

<script>
import KidHeader from '@/components/KidHeader.vue'
import KidFooter from '@/components/KidFooter.vue'

export default {
  name: 'CreateSchedule',
  components: { KidHeader, KidFooter },
  data() {
    return {
      profileId: this.$route.query.profileId,
      serialNumber: this.$route.query.serialNumber,
      scheduleId: this.$route.query.scheduleId,
      isEditMode: this.$route.query.edit === 'true',
      currentSchedule: {
        label: '',
        type: 'custom',
        days: [],
        slots: [{ startTime: '09:00', endTime: '17:00', mode: 'sleep', limit: 60, categories: [], restrictedApps: [] }],
        isActive: true
      },
      allDays: [
        { label: 'Monday', short: 'Mo', value: 'monday' },
        { label: 'Tuesday', short: 'Tu', value: 'tuesday' },
        { label: 'Wednesday', short: 'We', value: 'wednesday' },
        { label: 'Thursday', short: 'Th', value: 'thursday' },
        { label: 'Friday', short: 'Fr', value: 'friday' },
        { label: 'Saturday', short: 'Sa', value: 'saturday' },
        { label: 'Sunday', short: 'Su', value: 'sunday' }
      ],
      appCategories: [
        { 
          id: 'games', 
          name: 'Games', 
          icon: 'fas fa-gamepad',
          apps: [
            { id: 'game1', name: 'Candy Crush', packageName: 'com.king.candycrushsaga', enabled: false },
            { id: 'game2', name: 'PUBG Mobile', packageName: 'com.tencent.ig', enabled: false },
            { id: 'game3', name: 'Subway Surfers', packageName: 'com.kiloo.subwaysurf', enabled: false },
            { id: 'game4', name: 'Clash of Clans', packageName: 'com.supercell.clashofclans', enabled: false }
          ]
        },
        { 
          id: 'social', 
          name: 'Social Media', 
          icon: 'fas fa-users',
          apps: [
            { id: 'social1', name: 'Facebook', packageName: 'com.facebook.katana', enabled: false },
            { id: 'social2', name: 'Instagram', packageName: 'com.instagram.android', enabled: false },
            { id: 'social3', name: 'Twitter', packageName: 'com.twitter.android', enabled: false },
            { id: 'social4', name: 'TikTok', packageName: 'com.zhiliaoapp.musically', enabled: false }
          ]
        },
        { 
          id: 'entertainment', 
          name: 'Entertainment', 
          icon: 'fas fa-film',
          apps: [
            { id: 'ent1', name: 'YouTube', packageName: 'com.google.android.youtube', enabled: false },
            { id: 'ent2', name: 'Netflix', packageName: 'com.netflix.mediaclient', enabled: false },
            { id: 'ent3', name: 'Spotify', packageName: 'com.spotify.music', enabled: false },
            { id: 'ent4', name: 'Prime Video', packageName: 'com.amazon.avod.thirdpartyclient', enabled: false }
          ]
        },
        { 
          id: 'education', 
          name: 'Education', 
          icon: 'fas fa-graduation-cap',
          apps: [
            { id: 'edu1', name: 'Khan Academy', packageName: 'org.khanacademy.android', enabled: false },
            { id: 'edu2', name: 'Duolingo', packageName: 'com.duolingo', enabled: false },
            { id: 'edu3', name: 'Google Classroom', packageName: 'com.google.android.apps.classroom', enabled: false }
          ]
        },
        { 
          id: 'shopping', 
          name: 'Shopping', 
          icon: 'fas fa-shopping-cart',
          apps: [
            { id: 'shop1', name: 'Amazon', packageName: 'com.amazon.mShop.android.shopping', enabled: false },
            { id: 'shop2', name: 'Flipkart', packageName: 'com.flipkart.android', enabled: false },
            { id: 'shop3', name: 'eBay', packageName: 'com.ebay.mobile', enabled: false }
          ]
        },
        { 
          id: 'news', 
          name: 'News & Magazines', 
          icon: 'fas fa-newspaper',
          apps: [
            { id: 'news1', name: 'Google News', packageName: 'com.google.android.apps.magazines', enabled: false },
            { id: 'news2', name: 'BBC News', packageName: 'bbc.mobile.news.ww', enabled: false },
            { id: 'news3', name: 'CNN', packageName: 'com.cnn.mobile.android.phone', enabled: false }
          ]
        },
        { 
          id: 'productivity', 
          name: 'Productivity', 
          icon: 'fas fa-briefcase',
          apps: [
            { id: 'prod1', name: 'Microsoft Office', packageName: 'com.microsoft.office.officehubrow', enabled: false },
            { id: 'prod2', name: 'Google Drive', packageName: 'com.google.android.apps.docs', enabled: false },
            { id: 'prod3', name: 'Evernote', packageName: 'com.evernote', enabled: false }
          ]
        },
        { 
          id: 'communication', 
          name: 'Communication', 
          icon: 'fas fa-comments',
          apps: [
            { id: 'comm1', name: 'WhatsApp', packageName: 'com.whatsapp', enabled: false },
            { id: 'comm2', name: 'Telegram', packageName: 'org.telegram.messenger', enabled: false },
            { id: 'comm3', name: 'Messenger', packageName: 'com.facebook.orca', enabled: false }
          ]
        }
      ],
      expandedCategories: {},
      errors: {}
    };
  },
  computed: {
    isValidSchedule() {
      return this.currentSchedule.label.trim() !== '' &&
             this.currentSchedule.days.length > 0 &&
             this.currentSchedule.slots.length > 0 &&
             this.validateAllSlots();
    }
  },
  methods: {
    selectScheduleType(type) {
      this.currentSchedule.type = type;
      switch(type) {
        case 'weekday':
          this.currentSchedule.days = ['monday','tuesday','wednesday','thursday','friday'];
          break;
        case 'weekend':
          this.currentSchedule.days = ['saturday','sunday'];
          break;
        case 'everyday':
          this.currentSchedule.days = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'];
          break;
        default:
          break;
      }
    },
    toggleDay(day) {
      const idx = this.currentSchedule.days.indexOf(day);
      if (idx > -1) this.currentSchedule.days.splice(idx, 1);
      else this.currentSchedule.days.push(day);
      this.currentSchedule.type = 'custom';
    },
    addSlot() {
      this.currentSchedule.slots.push({
        startTime: '09:00',
        endTime: '17:00',
        mode: 'sleep',
        limit: 60,
        categories: [],
        restrictedApps: []
      });
    },
    removeSlot(index) {
      if (this.currentSchedule.slots.length > 1) {
        this.currentSchedule.slots.splice(index, 1);
      }
    },
    toggleCategoryExpand(slotIndex, categoryId) {
      const key = slotIndex + '-' + categoryId;
      this.expandedCategories[key] = !this.expandedCategories[key];
    },
    validateAllSlots() {
      return this.currentSchedule.slots.every(slot => {
        if (slot.mode === 'sleep') {
          return slot.startTime && slot.endTime && slot.startTime < slot.endTime;
        } else if (slot.mode === 'limit') {
          return slot.limit && slot.limit >= 5;
        } else if (slot.mode === 'apps') {
          return slot.startTime && slot.endTime && slot.startTime < slot.endTime && slot.categories.length > 0;
        }
        return false;
      });
    },
    validateSchedule() {
      this.errors = {};
      if (!this.currentSchedule.label.trim()) this.errors.label = 'Schedule name is required';
      if (this.currentSchedule.days.length === 0) this.errors.days = 'Please select at least one day';
      
      // Validate each slot
      let hasError = false;
      this.currentSchedule.slots.forEach((slot, index) => {
        if (slot.mode === 'sleep' && (!slot.startTime || !slot.endTime || slot.startTime >= slot.endTime)) {
          this.errors.slots = `Restriction #${index + 1}: Start time must be before end time`;
          hasError = true;
        }
        if (slot.mode === 'limit' && (!slot.limit || slot.limit < 5)) {
          this.errors.slots = `Restriction #${index + 1}: Daily limit must be at least 5 minutes`;
          hasError = true;
        }
        if (slot.mode === 'apps') {
          if (!slot.startTime || !slot.endTime || slot.startTime >= slot.endTime) {
            this.errors.slots = `Restriction #${index + 1}: Start time must be before end time`;
            hasError = true;
          }
          if (slot.categories.length === 0) {
            this.errors.slots = `Restriction #${index + 1}: Please select at least one app category`;
            hasError = true;
          }
        }
      });
      
      return Object.keys(this.errors).length === 0;
    },
    saveSchedule() {
      if (!this.validateSchedule()) return;
      
      // Prepare data object for API
      const schedulePayload = {
        profileId: this.profileId,
        serialNumber: this.serialNumber,
        schedule: {
          id: this.isEditMode ? this.scheduleId : null,
          label: this.currentSchedule.label.trim(),
          type: this.currentSchedule.type,
          days: this.currentSchedule.days,
          isActive: this.currentSchedule.isActive,
          restrictions: this.currentSchedule.slots.map((slot, index) => {
            const restriction = {
              order: index + 1,
              mode: slot.mode
            };

            // Add mode-specific fields
            if (slot.mode === 'sleep') {
              restriction.startTime = slot.startTime;
              restriction.endTime = slot.endTime;
            } else if (slot.mode === 'limit') {
              restriction.dailyLimitMinutes = slot.limit;
            } else if (slot.mode === 'apps') {
              restriction.startTime = slot.startTime;
              restriction.endTime = slot.endTime;
              restriction.categories = slot.categories;
              restriction.restrictedApps = slot.restrictedApps || [];
            }

            return restriction;
          }),
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }
      };
      
      console.log('Schedule Payload for API:', JSON.stringify(schedulePayload, null, 2));
      
      // TODO: Call API here
      // Example:
      // if (this.isEditMode) {
      //   KidsService.updateSchedule(schedulePayload)
      //     .then(response => {
      //       this.$toast.success('Schedule updated successfully!');
      //       this.$router.push({ path: '/schedule', query: { profileId: this.profileId, serialNumber: this.serialNumber } });
      //     })
      //     .catch(error => {
      //       this.$toast.error('Failed to update schedule');
      //       console.error(error);
      //     });
      // } else {
      //   KidsService.createSchedule(schedulePayload)
      //     .then(response => {
      //       this.$toast.success('Schedule created successfully!');
      //       this.$router.push({ path: '/schedule', query: { profileId: this.profileId, serialNumber: this.serialNumber } });
      //     })
      //     .catch(error => {
      //       this.$toast.error('Failed to create schedule');
      //       console.error(error);
      //     });
      // }
      
      alert(this.isEditMode ? 'Schedule updated successfully!' : 'Schedule created successfully!');
      this.$router.push({ path: '/schedule', query: { profileId: this.profileId, serialNumber: this.serialNumber } });
    },
    cancelSchedule() {
      if (confirm('Are you sure you want to cancel? Any unsaved changes will be lost.')) {
        this.$router.push({ path: '/schedule', query: { profileId: this.profileId, serialNumber: this.serialNumber } });
      }
    }
  },
  mounted() {
    // Open first category by default for first slot
    if (this.currentSchedule.slots.length > 0 && this.appCategories.length > 0) {
      this.expandedCategories['0-' + this.appCategories[0].id] = true;
    }
  }
};
</script>

<style scoped>
/* ---------- Base layout ---------- */
.back-home {
  display: block;
  width: 100%;
  margin-bottom: 12px;
  font-size: 0.95rem;
}

.card {
  border: none;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
}

/* ---------- Type Chips ---------- */
.type-chips {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.type-chip {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 999px;
  border: 1px solid #e6e6ee;
  background: #fafafa;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.18s ease;
  min-width: 92px;
  justify-content: center;
  color: #333;
}

.type-chip i {
  font-size: 14px;
  opacity: 0.9;
}

.type-chip .chip-label {
  font-weight: 600;
  font-size: 13px;
}

.type-chip:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 12px rgba(102,103,255,0.08);
}

.type-chip.active {
  background: linear-gradient(90deg,#6C63FF,#5E54E2);
  color: #fff;
  border-color: rgba(92,80,255,0.9);
  box-shadow: 0 8px 20px rgba(92,80,255,0.12);
}

/* ---------- Days bubbles ---------- */
.days-bubbles {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.day-bubble {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 2px solid #ddd;
  background: #f7f7f7;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 14px;
  color: #333;
  cursor: pointer;
  transition: all 0.15s ease;
}

.day-bubble.active {
  background: linear-gradient(135deg,#6C63FF,#5E54E2);
  color: #fff;
  border-color: transparent;
  box-shadow: 0 4px 12px rgba(108,99,255,0.3);
}

.day-bubble:hover {
  transform: translateY(-2px);
}

/* ---------- Compact Slot ---------- */
.compact-slot {
  border-radius: 10px;
  border: 1px solid #eef1fb;
  background: #fff;
  padding: 12px;
  box-shadow: 0 2px 6px rgba(0,0,0,0.05);
  position: relative;
}

.slot-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 10px;
  border-bottom: 1px solid #eef1fb;
}

.slot-number {
  font-weight: 700;
  font-size: 13px;
  color: #6C63FF;
}

.btn-delete-slot {
  background: #fee;
  color: #e11d48;
  border: none;
  padding: 4px 10px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s;
}

.btn-delete-slot:hover {
  background: #fdd;
}

.slot-row {
  display: flex;
  gap: 12px;
}

.slot-col {
  flex: 1;
}

.slot-col label {
  display: block;
  font-size: 12px;
  margin-bottom: 6px;
  color: #333;
}

/* mode selector */
.compact-mode {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
}

.mode-btn {
  flex: 1;
  min-width: 120px;
  padding: 8px 10px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  border: 1px solid #e6e6ee;
  background: #fafafa;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: all 0.12s ease;
}

.mode-btn.sleep-btn.active {
  background: #ef4444;
  color: #fff;
  border-color: #ef4444;
}

.mode-btn.limit-btn.active {
  background: #f59e0b;
  color: #fff;
  border-color: #f59e0b;
}

.mode-btn.apps-btn.active {
  background: #8b5cf6;
  color: #fff;
  border-color: #8b5cf6;
}

/* Mode notes/descriptions */
.mode-note {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 13px;
}

.mode-note i {
  margin-top: 2px;
  flex-shrink: 0;
}

.mode-note-sleep {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
}

.mode-note-limit {
  background: #fffbeb;
  border: 1px solid #fde68a;
  color: #92400e;
}

.mode-note-apps {
  background: #f5f3ff;
  border: 1px solid #ddd6fe;
  color: #6b21a8;
}

/* App Categories */
.app-categories {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.category-wrapper {
  border: 1px solid #eef1fb;
  border-radius: 8px;
  overflow: hidden;
}

.category-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  background: #fafafa;
  cursor: pointer;
  transition: background 0.2s;
}

.category-item:hover {
  background: #f0f0f5;
}

.category-item.expanded {
  background: #f5f3ff;
  border-bottom: 1px solid #eef1fb;
}

.category-info {
  display: flex;
  align-items: center;
  gap: 10px;
}

.category-icon {
  color: #6C63FF;
  font-size: 16px;
  width: 20px;
  text-align: center;
}

.category-name {
  font-size: 13px;
  font-weight: 600;
  color: #333;
}

.app-count {
  font-size: 11px;
  color: #999;
  margin-left: 4px;
}

.category-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.expand-icon {
  color: #6C63FF;
  font-size: 12px;
  transition: transform 0.2s;
}

.expand-icon.rotated {
  transform: rotate(180deg);
}

/* Apps List */
.apps-list {
  background: #fff;
  padding: 8px;
}

.app-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: #fafafa;
  border-radius: 6px;
  margin-bottom: 6px;
  border: 1px solid #f0f0f5;
}

.app-item:last-child {
  margin-bottom: 0;
}

.app-name {
  font-size: 13px;
  color: #555;
}

/* Add More Button */
.btn-add-more {
  width: 100%;
  padding: 10px;
  border-radius: 8px;
  border: 2px dashed #6C63FF;
  background: #f5f3ff;
  color: #6C63FF;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s;
}

.btn-add-more:hover {
  background: #ede9fe;
  transform: translateY(-2px);
}

/* inputs */
.form-control-sm {
  border-radius: 8px;
  padding: 6px 10px;
}

/* Custom Switch */
.custom-control-label {
  cursor: pointer;
}
</style>